import { useId } from "react";

import ExportablesTemplateHeaderSection from "@/components/AIComponents/ExportablesModal/templates/components/ExportablesTemplateHeaderSection";
import OrganizerDirections from "@/components/AIComponents/ExportablesModal/templates/components/OrganizerDirections/OrganizerDirections";
import { TemplateTextArea } from "@/components/AIComponents/ExportablesModal/templates/components/TemplateTextArea";
import { type MainIdeaFormData } from "@/types/models/Exportables.interface";
import {
  detailBoxCss,
  detailBoxTextareaCss,
  detailsRowCss,
  mainIdeaBoxCss,
  mainIdeaGridCss,
  mainIdeaParagraphCss,
  mainIdeaTextareaCss,
} from "./MainIdeaOrganizer.styles";

import type { TemplateProps } from "@/components/AIComponents/ExportablesModal/templates/registry";

const MainIdeaOrganizer = ({
  formData,
  onChange,
  title = "Main idea and details",
  isStudent = false,
}: TemplateProps<MainIdeaFormData>) => {
  const id = useId();
  const MAIN_IDEA_MAX_LENGTH = 125; // Updated per Jon's specification
  const MAIN_IDEA_DETAIL_MAX_LENGTH = 250; // New constant for detail max length
  const getDefaultDirections = (articleTitle: string) =>
    `Read the article "${articleTitle}" and identify three key details that support the central idea noted below. Explain how each detail you selected supports the central idea.`;
  const {
    article_title: articleTitle = "",
    main_idea: mainIdea = "",
    directions = getDefaultDirections(articleTitle),
    detail1 = "",
    detail2 = "",
    detail3 = "",
  } = formData || {};

  const detailKeys = [
    { key: "detail1", value: detail1 },
    { key: "detail2", value: detail2 },
    { key: "detail3", value: detail3 },
  ] as const;

  const handleChange = (field: keyof MainIdeaFormData, value: string) => {
    if (onChange) {
      const currentDirections =
        formData?.directions ||
        getDefaultDirections(formData?.article_title ?? "");
      onChange({
        article_title: formData?.article_title ?? "",
        main_idea: formData?.main_idea ?? "",
        directions: field === "directions" ? value : currentDirections,
        detail1: formData?.detail1 ?? "",
        detail2: formData?.detail2 ?? "",
        detail3: formData?.detail3 ?? "",
        [field]: value,
      });
    }
  };

  // --- Render helpers for each section ---
  const renderTitleSection = () => (
    <ExportablesTemplateHeaderSection
      templateTitle={title}
      articleTitle={articleTitle}
      directions={
        <OrganizerDirections
          name="Main Idea"
          value={directions}
          viewName="Central Idea"
          handleChange={(e) => handleChange("directions", e.target.value)}
          isStudent={isStudent}
        />
      }
    />
  );

  const renderMainIdeaSection = () => (
    <div css={mainIdeaBoxCss}>
      {isStudent ? (
        <span>Central Idea</span>
      ) : (
        <label htmlFor={id}>Central Idea</label>
      )}
      <TemplateTextArea
        value={mainIdea}
        readOnly={isStudent}
        maxLength={MAIN_IDEA_MAX_LENGTH}
      >
        <TemplateTextArea.TextArea
          data-qa-selector="main-idea"
          id={id}
          onChange={(e) => handleChange("main_idea", e.target.value)}
          css={mainIdeaTextareaCss}
          aria-label="Central Idea"
          placeholder="Write a clear, concise central idea"
        />
        <TemplateTextArea.Paragraph css={mainIdeaParagraphCss} />
        <TemplateTextArea.CharacterCount />
      </TemplateTextArea>
    </div>
  );

  const renderDetailsSection = () => (
    <div css={detailsRowCss}>
      {detailKeys.map(({ key, value }, idx) => (
        <div css={detailBoxCss} key={key}>
          <label htmlFor={`detail-${idx + 1}`}>{`Detail #${idx + 1}`}</label>
          <TemplateTextArea
            value={value}
            maxLength={MAIN_IDEA_DETAIL_MAX_LENGTH}
          >
            <TemplateTextArea.TextArea
              data-qa-selector={`detail-${idx + 1}`}
              id={`detail-${idx + 1}`}
              onChange={(e) => handleChange(key, e.target.value)}
              aria-label={`Detail #${idx + 1}`}
              css={detailBoxTextareaCss}
            />
            <TemplateTextArea.CharacterCount />
          </TemplateTextArea>
        </div>
      ))}
    </div>
  );

  return (
    <div css={mainIdeaGridCss} aria-label="Central Idea Organizer">
      {renderTitleSection()}
      {renderMainIdeaSection()}
      {renderDetailsSection()}
    </div>
  );
};

export default MainIdeaOrganizer;
